// src/lib/api/team/employees.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired, validateEmail, validatePhone, sanitizeInput } from '../shared/validation'

/**
 * Get all employees for a company
 * Migrated from supabaseApi.js lines 1732-1765
 */
const _getEmployees = async (companyId = null) => {
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    console.warn('No company ID found, returning empty employees list')
    return createSuccessResponse([])
  }
  
  const { data, error } = await supabase
    .from('employees')
    .select(`
      id,
      company_id,
      first_name,
      last_name,
      email,
      phone,
      role,
      department,
      status,
      hourly_rate,
      skills,
      created_at,
      updated_at
    `)
    .eq('company_id', resolvedCompanyId)
    .in('status', ['active', 'pending'])
    .order('first_name', { ascending: true })
  
  if (error) throw error
  
  // Transform the data to match the expected format
  const transformedData = (data || []).map(emp => ({
    id: emp.id,
    name: `${emp.first_name} ${emp.last_name}`,
    email: emp.email,
    phone: emp.phone,
    role: emp.role,
    department: emp.department,
    employeeId: emp.id, // Use the primary key as employee ID
    status: emp.status,
    hireDate: emp.created_at, // Use created_at as hire date
    hourlyRate: emp.hourly_rate,
    skills: emp.skills || [],
    companyId: emp.company_id,
    createdAt: emp.created_at,
    updatedAt: emp.updated_at
  }))
  
  return createSuccessResponse(transformedData)
}

/**
 * Create a new employee
 * Migrated from supabaseApi.js lines 1767-1800
 */
const _createEmployee = async (employeeData) => {
  validateRequired(employeeData.email, 'Email')
  validateEmail(employeeData.email)
  
  if (employeeData.phone) {
    validatePhone(employeeData.phone)
  }
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  // Transform the incoming data to match the new table structure
  const transformedData = {
    company_id: companyId,
    first_name: sanitizeInput(employeeData.firstName || employeeData.name?.split(' ')[0] || ''),
    last_name: sanitizeInput(employeeData.lastName || employeeData.name?.split(' ').slice(1).join(' ') || ''),
    email: sanitizeInput(employeeData.email),
    phone: employeeData.phone ? sanitizeInput(employeeData.phone) : null,
    role: employeeData.role || 'Technician',
    department: employeeData.department ? sanitizeInput(employeeData.department) : null,
    hourly_rate: employeeData.hourlyRate || 0.00,
    skills: employeeData.skills || [],
    status: 'active'
  }
  
  const { data, error } = await supabase
    .from('employees')
    .insert([transformedData])
    .select()
    .single()
  
  if (error) {
    if (error.message?.includes('Employee slot limit reached')) {
      throw new Error('EMPLOYEE_SLOT_LIMIT_REACHED: ' + error.message)
    }
    throw error
  }

  // Return the data in the expected format
  return {
    id: data.id,
    name: `${data.first_name} ${data.last_name}`,
    email: data.email,
    phone: data.phone,
    role: data.role,
    department: data.department,
    employeeId: data.id, // Use the primary key as employee ID
    status: data.status,
    hireDate: data.created_at, // Use created_at as hire date
    hourlyRate: data.hourly_rate,
    skills: data.skills || [],
    companyId: data.company_id,
    createdAt: data.created_at,
    updatedAt: data.updated_at
  }
}

/**
 * Update an existing employee
 */
const _updateEmployee = async (employeeId, employeeData) => {
  validateRequired(employeeId, 'Employee ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  if (employeeData.email) {
    validateEmail(employeeData.email)
  }
  
  if (employeeData.phone) {
    validatePhone(employeeData.phone)
  }
  
  const sanitizedData = {
    ...employeeData,
    company_id: companyId
  }
  
  if (sanitizedData.first_name) {
    sanitizedData.first_name = sanitizeInput(sanitizedData.first_name)
  }
  if (sanitizedData.last_name) {
    sanitizedData.last_name = sanitizeInput(sanitizedData.last_name)
  }
  if (sanitizedData.email) {
    sanitizedData.email = sanitizeInput(sanitizedData.email)
  }
  if (sanitizedData.phone) {
    sanitizedData.phone = sanitizeInput(sanitizedData.phone)
  }
  if (sanitizedData.department) {
    sanitizedData.department = sanitizeInput(sanitizedData.department)
  }
  
  const { data, error } = await supabase
    .from('employees')
    .update(sanitizedData)
    .eq('id', employeeId)
    .eq('company_id', companyId)
    .select()
    .single()
  
  if (error) throw error
  
  // Return the data in the expected format
  return {
    id: data.id,
    name: `${data.first_name} ${data.last_name}`,
    email: data.email,
    phone: data.phone,
    role: data.role,
    department: data.department,
    employeeId: data.id,
    status: data.status,
    hireDate: data.created_at,
    hourlyRate: data.hourly_rate,
    skills: data.skills || [],
    companyId: data.company_id,
    createdAt: data.created_at,
    updatedAt: data.updated_at
  }
}

/**
 * Delete an employee
 * Migrated from supabaseApi.js lines 1850-1860
 */
const _deleteEmployee = async (employeeId) => {
  validateRequired(employeeId, 'Employee ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { error } = await supabase
    .from('employees')
    .delete()
    .eq('id', employeeId)
    .eq('company_id', companyId)
  
  if (error) throw error
  return { success: true }
}

/**
 * Get employee by ID
 */
const _getEmployeeById = async (employeeId) => {
  validateRequired(employeeId, 'Employee ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { data, error } = await supabase
    .from('employees')
    .select(`
      id,
      company_id,
      first_name,
      last_name,
      email,
      phone,
      role,
      department,
      status,
      hourly_rate,
      skills,
      created_at,
      updated_at
    `)
    .eq('id', employeeId)
    .eq('company_id', companyId)
    .single()
  
  if (error) throw error
  
  // Return the data in the expected format
  return {
    id: data.id,
    name: `${data.first_name} ${data.last_name}`,
    email: data.email,
    phone: data.phone,
    role: data.role,
    department: data.department,
    employeeId: data.id,
    status: data.status,
    hireDate: data.created_at,
    hourlyRate: data.hourly_rate,
    skills: data.skills || [],
    companyId: data.company_id,
    createdAt: data.created_at,
    updatedAt: data.updated_at
  }
}

/**
 * Get employees by role
 */
const _getEmployeesByRole = async (role, companyId = null) => {
  validateRequired(role, 'Role')
  
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    return createSuccessResponse([])
  }
  
  const { data, error } = await supabase
    .from('employees')
    .select(`
      id,
      company_id,
      first_name,
      last_name,
      email,
      phone,
      role,
      department,
      status,
      hourly_rate,
      skills,
      created_at,
      updated_at
    `)
    .eq('company_id', resolvedCompanyId)
    .eq('role', role)
    .eq('status', 'active')
    .order('first_name', { ascending: true })
  
  if (error) throw error
  
  // Transform the data to match the expected format
  const transformedData = (data || []).map(emp => ({
    id: emp.id,
    name: `${emp.first_name} ${emp.last_name}`,
    email: emp.email,
    phone: emp.phone,
    role: emp.role,
    department: emp.department,
    employeeId: emp.id,
    status: emp.status,
    hireDate: emp.created_at,
    hourlyRate: emp.hourly_rate,
    skills: emp.skills || [],
    companyId: emp.company_id,
    createdAt: emp.created_at,
    updatedAt: emp.updated_at
  }))
  
  return createSuccessResponse(transformedData)
}

/**
 * Update employee status
 */
const _updateEmployeeStatus = async (employeeId, status) => {
  validateRequired(employeeId, 'Employee ID')
  validateRequired(status, 'Status')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { data, error } = await supabase
    .from('employees')
    .update({ 
      status, 
      updated_at: new Date().toISOString() 
    })
    .eq('id', employeeId)
    .eq('company_id', companyId)
    .select()
    .single()
  
  if (error) throw error
  return data
}

// Export wrapped functions with error handling
export const getEmployees = withErrorHandling(_getEmployees, 'fetch employees', 'employees')
export const createEmployee = withErrorHandling(_createEmployee, 'create employee', 'employee')
export const updateEmployee = withErrorHandling(_updateEmployee, 'update employee', 'employee')
export const deleteEmployee = withErrorHandling(_deleteEmployee, 'delete employee', 'employee')
export const getEmployeeById = withErrorHandling(_getEmployeeById, 'get employee by ID', 'employee')
export const getEmployeesByRole = withErrorHandling(_getEmployeesByRole, 'get employees by role', 'employees')
export const updateEmployeeStatus = withErrorHandling(_updateEmployeeStatus, 'update employee status', 'employee')